import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SerialService } from './serial.service';
import { UpgradeFile } from './file-source.interface';

export type SendProgress = {
    currentFileIndex: number;
    totalFiles: number;
    currentFileName: string;
    currentFileBytesTransferred: number;
    currentFileSize: number;
    overallBytesTransferred: number;
    overallTotalBytes: number;
    isSending: boolean;
    error: string | null;
};

const SZ_CODES = {
    SOH: 0x01,
    STX: 0x02,
    EOT: 0x04,
    ACK: 0x06,
    NAK: 0x15,
    CAN: 0x18,
    C: 0x43
};

@Injectable({
    providedIn: 'root'
})
export class YmodemSendService {
    private readonly serialService = inject(SerialService);

    private progressSubject = new BehaviorSubject<SendProgress>({
        currentFileIndex: 0,
        totalFiles: 0,
        currentFileName: '',
        currentFileBytesTransferred: 0,
        currentFileSize: 0,
        overallBytesTransferred: 0,
        overallTotalBytes: 0,
        isSending: false,
        error: null
    });

    public progress$: Observable<SendProgress> = this.progressSubject.asObservable();

    public resetProgress(): void {
        this.progressSubject.next({
            currentFileIndex: 0,
            totalFiles: 0,
            currentFileName: '',
            currentFileBytesTransferred: 0,
            currentFileSize: 0,
            overallBytesTransferred: 0,
            overallTotalBytes: 0,
            isSending: false,
            error: null
        });
    }

    public async sendBatch(files: UpgradeFile[]): Promise<void> {
        console.log(`[YModem] Starting batch transfer of ${files.length} files`);
        const progress = this.progressSubject.value;
        progress.totalFiles = files.length;
        progress.isSending = true;
        progress.error = null;
        progress.overallTotalBytes = files.reduce((sum, f) => sum + f.size, 0);
        this.progressSubject.next(progress);

        try {
            // Buffered reading is already started at connection time
            // Clear the buffer from any old connection-phase responses (e.g., echoed 'C')
            console.log('[YModem] Clearing buffer from connection phase...');
            this.serialService.drainBuffer();

            // Give device a moment to settle before waiting for ready signal
            await new Promise((resolve) => setTimeout(resolve, 30));

            // Wait for device to respond with 'C' (ready signal)
            // The initial 'C' prompt was already sent at connection time
            await this.waitForReadyCommand();
            console.log('[YModem] Device ready, starting file transfer');

            for (let i = 0; i < files.length; i++) {
                progress.currentFileIndex = i;
                progress.currentFileName = files[i].name;
                progress.currentFileSize = files[i].size;
                progress.currentFileBytesTransferred = 0;
                this.progressSubject.next(progress);

                console.log(
                    `[YModem] Sending file ${i + 1}/${files.length}: ${files[i].name} (${files[i].size} bytes)`
                );
                await this.sendFile(files[i]);
                console.log(`[YModem] File ${i + 1}/${files.length} transfer complete`);
            }

            // Send final null block to signal end of batch
            console.log('[YModem] Sending final null block');
            const nullBlock = this.buildNullBlock();
            await this.sendPacket(nullBlock);

            // After null block, device will send 'C' asking for next file (standard Ymodem)
            console.log('[YModem] Waiting for C after null block');
            await this.waitForReadyCommand();

            // End of transfer - handle EOT handshake per Ymodem spec
            // Some devices use standard 2-EOT handshake (NAK then ACK)
            // Others skip NAK and send ACK immediately (ALWAYS_ACK_EOT mode)
            // Some devices respond with C (ready for next batch) = success
            console.log('[YModem] Sending first EOT for batch end');
            await this.serialService.sendData(new Uint8Array([SZ_CODES.EOT]));

            const eotResponse = await this.serialService.waitForAckOrNakOrC(5000);
            if (eotResponse === SZ_CODES.NAK) {
                console.log('[YModem] Received NAK, sending second EOT (standard behavior)');
                await this.serialService.sendData(new Uint8Array([SZ_CODES.EOT]));
                await this.waitForByte(SZ_CODES.ACK, 5000);
            } else if (eotResponse === SZ_CODES.ACK) {
                console.log('[YModem] Received ACK on first EOT (device skips NAK)');
                // Device accepted first EOT, no need for second EOT
            } else if (eotResponse === SZ_CODES.C) {
                console.log('[YModem] Received C in response to EOT (device ready for next batch = success)');
                // Device is ready for next batch, which means transfer complete for us
            }

            progress.isSending = false;
            this.progressSubject.next(progress);

            // Disconnect from the device
            console.log('[YModem] Transfer complete, disconnecting device');
            await this.serialService.disconnect();
        } catch (error) {
            progress.isSending = false;
            progress.error = error instanceof Error ? error.message : 'Unknown error';
            this.progressSubject.next(progress);

            // Disconnect even on error
            console.log('[YModem] Transfer failed, disconnecting device');
            await this.serialService.disconnect();
            throw error;
        }
    }

    private async sendFile(file: UpgradeFile): Promise<void> {
        console.log(`[YModem] Building block 0 for file: ${file.name}`);
        // Build and send block 0 (filename + size)
        const block0 = this.buildBlock0(file);
        console.log(`[YModem] Sending block 0 (file header)`);

        // Send block 0 and wait for ACK + C (per Ymodem spec step 3)
        await this.sendBlock0Packet(block0);
        console.log('[YModem] Block 0 acknowledged, starting data blocks');

        // Send file data in chunks - use 1024-byte packets (STX) as device expects
        const chunkSize = 1024; // Use 1024-byte packets (STX) per device specification
        let blockNumber = 1;

        for (let offset = 0; offset < file.data.length; offset += chunkSize) {
            const chunk = file.data.slice(offset, Math.min(offset + chunkSize, file.data.length));
            const dataPacket = this.buildDataPacket(chunk, blockNumber);

            //console.log(`[YModem] Sending data block ${blockNumber} (${chunk.length} bytes)`);
            await this.sendPacket(dataPacket);

            const progress = this.progressSubject.value;
            progress.currentFileBytesTransferred += chunk.length;
            progress.overallBytesTransferred += chunk.length;
            this.progressSubject.next(progress);

            blockNumber++;
        }

        // End of file - handle EOT handshake
        // Some devices use standard 2-EOT handshake (NAK then ACK)
        // Others skip NAK and send ACK immediately (ALWAYS_ACK_EOT mode)
        // Some devices respond with C (ready for next file) = success
        console.log('[YModem] Sending first EOT');
        await this.serialService.sendData(new Uint8Array([SZ_CODES.EOT]));

        const eotResponse = await this.serialService.waitForAckOrNakOrC(5000);
        if (eotResponse === SZ_CODES.NAK) {
            console.log('[YModem] Received NAK, sending second EOT (standard behavior)');
            await this.serialService.sendData(new Uint8Array([SZ_CODES.EOT]));
            await this.waitForByte(SZ_CODES.ACK, 5000);
        } else if (eotResponse === SZ_CODES.ACK) {
            console.log('[YModem] Received ACK on first EOT (device skips NAK)');
            // Device accepted first EOT, no need for second EOT
        } else if (eotResponse === SZ_CODES.C) {
            console.log('[YModem] Received C in response to EOT (device ready for next file)');
            // C already consumed from buffer by waitForAckOrNakOrC, just continue
        }

        // After EOT handshake, if device sent C, we don't need to wait for it again
        // If device sent ACK/NAK, wait for C to signal ready for next file
        if (eotResponse !== SZ_CODES.C) {
            await this.waitForReadyCommand();
        }
    }

    private buildBlock0(file: UpgradeFile): Uint8Array {
        // Ymodem block 0 format: SOH + block# + block#' + filename\0filesize + padding + CRC16
        const filenameBytes = new TextEncoder().encode(file.name);
        const fileSizeStr = file.size.toString();
        const fileSizeBytes = new TextEncoder().encode(fileSizeStr);

        // Payload: filename\0filesize + padding
        const payloadSize = 128;
        const payload = new Uint8Array(payloadSize);
        payload.set(filenameBytes, 0);
        payload[filenameBytes.length] = 0; // null terminator
        payload.set(fileSizeBytes, filenameBytes.length + 1);
        // Rest is zero-padded (already is from Uint8Array initialization)

        // Build packet: SOH + blockNum + invBlockNum + payload + CRC16
        const packet = new Uint8Array(payloadSize + 5); // 1 header + 2 block bytes + 2 CRC = 5 extra bytes
        packet[0] = SZ_CODES.SOH; // Header
        packet[1] = 0; // Block number
        packet[2] = 255; // Inverted block number (255 = ~0)
        packet.set(payload, 3); // Payload
        const crc = this.calculateCrc16(payload);
        packet[payloadSize + 3] = (crc >> 8) & 0xff;
        packet[payloadSize + 4] = crc & 0xff;

        const packetPreview = Array.from(packet)
            .map((b) => `0x${b.toString(16).toUpperCase().padStart(2, '0')}`)
            .slice(0, 30)
            .join(' ');
        console.log(
            `[YModem] Block 0: filename="${file.name}", size=${file.size}, CRC16=0x${crc.toString(16).toUpperCase().padStart(4, '0')}`
        );
        console.log(`[YModem] Block 0 bytes (first 30): ${packetPreview}...`);
        return packet;
    }

    private buildNullBlock(): Uint8Array {
        // Final null block: SOH 00 FF [128 null bytes] CRC CRC
        // Signals end of batch transfer
        const payloadSize = 128;
        const payload = new Uint8Array(payloadSize); // All zeros

        const packet = new Uint8Array(payloadSize + 5); // 1 header + 2 block bytes + 2 CRC = 5 extra bytes
        packet[0] = SZ_CODES.SOH; // Header
        packet[1] = 0; // Block number
        packet[2] = 255; // Inverted block number
        packet.set(payload, 3); // All null bytes
        const crc = this.calculateCrc16(payload);
        packet[payloadSize + 3] = (crc >> 8) & 0xff;
        packet[payloadSize + 4] = crc & 0xff;

        return packet;
    }

    private buildDataPacket(data: Uint8Array, blockNumber: number): Uint8Array {
        // Use STX for 1024-byte packets per device specification
        const header = SZ_CODES.STX;
        const payloadSize = 1024;

        // Pad data to 1024 bytes with null bytes (0x00) for binary files
        const payload = new Uint8Array(payloadSize); // Already zero-filled by default
        payload.set(data, 0);
        // Note: Uint8Array is already zero-filled, no need to explicitly pad
        // (Unlike Xmodem text mode which uses 0x1A/Ctrl-Z, binary mode uses 0x00)

        // Build packet: STX + blockNum + invBlockNum + payload + CRC16
        const packet = new Uint8Array(payloadSize + 5); // 1 header + 2 block bytes + 2 CRC = 5 extra bytes
        packet[0] = header;
        packet[1] = blockNumber & 0xff;
        packet[2] = ~blockNumber & 0xff;
        packet.set(payload, 3);
        const crc = this.calculateCrc16(payload);
        packet[payloadSize + 3] = (crc >> 8) & 0xff;
        packet[payloadSize + 4] = crc & 0xff;

        return packet;
    }

    private async sendBlock0Packet(packet: Uint8Array): Promise<void> {
        const packetStr = Array.from(packet.slice(0, Math.min(20, packet.length)))
            .map((b) => `0x${b.toString(16).toUpperCase()}`)
            .join(', ');
        const truncated = packet.length > 20 ? '...' : '';
        console.log(`[YModem] Sending Block 0 (${packet.length} bytes): [${packetStr}${truncated}]`);
        await this.serialService.sendData(packet);

        // Device needs a moment to process Block 0 and log the received filename
        console.log('[YModem] Waiting for device to process Block 0...');
        await new Promise((resolve) => setTimeout(resolve, 150));

        // Device may have sent log messages and ANSI sequences while processing Block 0
        // Drain the buffer to clear them out before waiting for ACK
        console.log('[YModem] Draining buffer to clear log messages...');
        this.serialService.drainBuffer();
        console.log('[YModem] Buffer cleared, ready to receive ACK for Block 0');
    }

    private async sendPacket(packet: Uint8Array): Promise<void> {
        const maxRetries = 10;
        let retryCount = 0;

        while (retryCount <= maxRetries) {
            // const packetStr = Array.from(packet.slice(0, Math.min(20, packet.length)))
            //     .map((b) => `0x${b.toString(16).toUpperCase()}`)
            //     .join(', ');
            // const truncated = packet.length > 20 ? '...' : '';
            // const retryStr = retryCount > 0 ? ` (retry ${retryCount}/${maxRetries})` : '';
            // console.log(`[YModem] Sending ${packet.length} bytes${retryStr}: [${packetStr}${truncated}]`);
            await this.serialService.sendData(packet);

            // Immediately wait for ACK or NAK from receiver
            // No extra delay needed - transmission time is already substantial at 9600 baud
            try {
                const response = await this.waitForAckOrNak(15000);
                if (response === SZ_CODES.ACK) {
                    //console.log('[YModem] ✓ Packet acknowledged');
                    return; // Success!
                } else if (response === SZ_CODES.NAK) {
                    console.log(`[YModem] ✗ Packet rejected (NAK), retrying... (${retryCount + 1}/${maxRetries})`);
                    retryCount++;
                    continue; // Retry the packet
                }
            } catch {
                console.log(`[YModem] ✗ No response, retrying... (${retryCount + 1}/${maxRetries})`);
                retryCount++;
                continue;
            }
        }

        throw new Error(`Failed to send packet after ${maxRetries} retries`);
    }

    private async waitForByte(expectedByte: number, timeoutMs: number): Promise<void> {
        // Delegate to SerialService to wait for the byte
        await this.serialService.waitForByte(expectedByte, timeoutMs);
    }

    private async waitForReadyCommand(): Promise<void> {
        // Wait for device to send 'C' confirming it's ready to receive
        // The 'C' prompt was already sent at connection time to activate the device
        console.log('[YModem] Waiting for device to send C (ready signal)...');
        const timeout = 30000; // 30 second timeout

        try {
            await this.waitForByte(SZ_CODES.C, timeout);
            console.log('[YModem] ✓ Device sent C, confirmed ready to receive');
            return;
        } catch {
            throw new Error(
                `Device did not send ready signal (C) within ${timeout}ms - device may not be in receive mode`
            );
        }
    }

    private async waitForAckOrNak(timeoutMs: number): Promise<number> {
        // Wait for either ACK (0x06) or NAK (0x15)
        return await this.serialService.waitForAckOrNak(timeoutMs);
    }

    private calculateCrc16(data: Uint8Array): number {
        let crc = 0;
        const poly = 0x1021;

        for (const byte of data) {
            crc = crc ^ (byte << 8);
            for (let i = 0; i < 8; i++) {
                crc = crc << 1;
                if (crc & 0x10000) {
                    crc = (crc ^ poly) & 0xffff;
                }
            }
        }

        // const crcHi = (crc >> 8) & 0xff;
        // const crcLo = crc & 0xff;
        // console.log(
        //     `[YModem] CRC16 calculation: data length=${data.length}, crc=0x${crc.toString(16).toUpperCase()} (bytes: 0x${crcHi.toString(16).toUpperCase()} 0x${crcLo.toString(16).toUpperCase()})`
        // );

        return crc;
    }
}
